import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { useApp } from "../../context/AppContext";
import { audit } from "../../lib/audit";
import {
  CheckCircle2,
  X,
  Edit2,
  Clock,
  AlertTriangle,
  RefreshCw,
  ClipboardList,
  ArrowLeft,
  User,
} from "lucide-react";

const formatDataHoraLocal = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function FilaAprovacao() {
  const { user } = useApp();
  const navigate = useNavigate();

  const [ocorrencias, setOcorrencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { tipo: "editar"|"rejeitar", ocorrencia }

  const [textoEditado, setTextoEditado] = useState("");
  const [motivoRejeicao, setMotivoRejeicao] = useState("");
  const [salvando, setSalvando] = useState(false);

  const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";

  const carregar = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("liberty_ocorrencias")
      .select("*")
      .eq("empresa_id", EMPRESA_ID)
      .eq("status", "pendente")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("[Supabase] FilaAprovacao:", error.message);
    } else {
      setOcorrencias(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    carregar();
    const id = setInterval(() => carregar(), 30000);
    return () => clearInterval(id);
  }, [carregar]);

  const abrirEditar = (oc) => {
    setTextoEditado(oc.descricao || "");
    setModal({ tipo: "editar", ocorrencia: oc });
  };

  const abrirRejeitar = (oc) => {
    setMotivoRejeicao("");
    setModal({ tipo: "rejeitar", ocorrencia: oc });
  };

  const fecharModal = () => {
    setModal(null);
    setTextoEditado("");
    setMotivoRejeicao("");
  };

  const aprovar = async (oc) => {
    setSalvando(true);
    const { error } = await supabase
      .from("liberty_ocorrencias")
      .update({
        status: "aprovada",
        aprovado_por: user?.nome || "supervisor",
        aprovado_em: new Date().toISOString(),
      })
      .eq("id", oc.id);

    setSalvando(false);
    if (error) { console.error("[Supabase] aprovar:", error.message); return; }
    audit({
      usuario_nome: user?.nome,
      usuario_role: user?.role,
      acao: "ocorrencia_aprovada",
      modulo: "Aprovações",
      tabela: "liberty_ocorrencias",
      descricao: `Ocorrência de ${oc.vigilante_nome || "vigilante"} aprovada por ${user?.nome}`,
    });
    setOcorrencias((prev) => prev.filter((o) => o.id !== oc.id));
  };

  const editarEAprovar = async () => {
    if (!textoEditado.trim() || !modal) return;
    setSalvando(true);
    const oc = modal.ocorrencia;

    const { error } = await supabase
      .from("liberty_ocorrencias")
      .update({
        status: "aprovada",
        texto_original: oc.descricao,
        texto_editado: textoEditado.trim(),
        aprovado_por: user?.nome || "supervisor",
        aprovado_em: new Date().toISOString(),
      })
      .eq("id", oc.id);

    setSalvando(false);
    if (error) { console.error("[Supabase] editarAprovar:", error.message); return; }
    audit({
      usuario_nome: user?.nome,
      usuario_role: user?.role,
      acao: "ocorrencia_aprovada_editada",
      modulo: "Aprovações",
      tabela: "liberty_ocorrencias",
      descricao: `Ocorrência de ${oc.vigilante_nome || "vigilante"} aprovada com edição por ${user?.nome}`,
    });
    setOcorrencias((prev) => prev.filter((o) => o.id !== oc.id));
    fecharModal();
  };

  const rejeitar = async () => {
    if (!motivoRejeicao.trim() || !modal) return;
    setSalvando(true);
    const oc = modal.ocorrencia;

    const { error } = await supabase
      .from("liberty_ocorrencias")
      .update({
        status: "rejeitada",
        motivo_rejeicao: motivoRejeicao.trim(),
        aprovado_por: user?.nome || "supervisor",
        aprovado_em: new Date().toISOString(),
      })
      .eq("id", oc.id);

    setSalvando(false);
    if (error) { console.error("[Supabase] rejeitar:", error.message); return; }
    audit({
      usuario_nome: user?.nome,
      usuario_role: user?.role,
      acao: "ocorrencia_rejeitada",
      modulo: "Aprovações",
      tabela: "liberty_ocorrencias",
      descricao: `Ocorrência de ${oc.vigilante_nome || "vigilante"} rejeitada por ${user?.nome}. Motivo: ${motivoRejeicao.trim()}`,
    });
    setOcorrencias((prev) => prev.filter((o) => o.id !== oc.id));
    fecharModal();
  };

  return (
    <div
      className="min-h-screen bg-[#f4f5f8]"
      style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
    >
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#333A56] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-white"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <p className="text-xs text-white/60">Supervisor</p>
            <p className="text-sm font-bold text-white">Fila de Aprovação</p>
          </div>
        </div>
        <button
          onClick={carregar}
          className="w-9 h-9 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-white"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Resumo */}
        <div className="rounded-2xl bg-white border border-[#e8eaf0] p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
            <ClipboardList className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <p className="text-xs text-[#64748b]">Ocorrências pendentes</p>
            <p className="text-2xl font-bold text-[#333A56]">
              {loading ? "…" : ocorrencias.length}
            </p>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12 text-[#64748b] text-sm">
            Carregando…
          </div>
        )}

        {/* Vazia */}
        {!loading && ocorrencias.length === 0 && (
          <div className="rounded-2xl bg-white border border-[#e8eaf0] p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <p className="font-semibold text-[#333A56]">Fila vazia</p>
            <p className="text-sm text-[#64748b] mt-1">
              Nenhuma ocorrência aguardando aprovação.
            </p>
          </div>
        )}

        {/* Cards */}
        {!loading &&
          ocorrencias.map((oc) => (
            <div
              key={oc.id}
              className="rounded-2xl bg-white border border-[#e8eaf0] p-5 space-y-4"
            >
              {/* Cabeçalho */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#333A56] text-sm">
                      {oc.tipo || "Ocorrência"}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-[#64748b]">
                      <User className="w-3 h-3" />
                      {oc.vigilante_nome || "Vigilante"}
                      <span className="text-[#e8eaf0]">·</span>
                      <Clock className="w-3 h-3" />
                      {formatDataHoraLocal(oc.created_at)}
                    </div>
                  </div>
                </div>
                <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold">
                  Pendente
                </span>
              </div>

              {/* Descrição */}
              <p className="text-sm text-[#333A56] leading-relaxed bg-[#f4f5f8] rounded-xl px-4 py-3">
                {oc.descricao || "Sem descrição."}
              </p>

              {/* Ações */}
              <div className="flex gap-2">
                <button
                  onClick={() => aprovar(oc)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl bg-emerald-600 text-white text-sm font-semibold active:scale-[0.98] transition"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Aprovar
                </button>
                <button
                  onClick={() => abrirEditar(oc)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold active:scale-[0.98] transition"
                >
                  <Edit2 className="w-4 h-4" />
                  Editar
                </button>
                <button
                  onClick={() => abrirRejeitar(oc)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl bg-red-600 text-white text-sm font-semibold active:scale-[0.98] transition"
                >
                  <X className="w-4 h-4" />
                  Rejeitar
                </button>
              </div>
            </div>
          ))}
      </main>

      {/* Modal Editar e Aprovar */}
      {modal?.tipo === "editar" && (
        <div className="fixed inset-0 z-[999] bg-black/50 backdrop-blur-sm flex items-end justify-center p-4">
          <div className="w-full max-w-lg rounded-3xl bg-white border border-[#e8eaf0] overflow-hidden shadow-2xl">
            <div className="px-5 pt-5 pb-3 flex items-center justify-between">
              <p className="font-bold text-[#333A56]">✏️ Editar e Aprovar</p>
              <button
                onClick={fecharModal}
                className="w-8 h-8 rounded-lg bg-[#f4f5f8] flex items-center justify-center text-[#64748b]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 pb-2">
              <p className="text-xs text-[#64748b] mb-1">Texto original:</p>
              <p className="text-sm text-[#64748b] bg-[#f4f5f8] rounded-xl px-4 py-3 leading-relaxed">
                {modal.ocorrencia.descricao}
              </p>
            </div>

            <div className="px-5 pb-5 space-y-4">
              <div>
                <p className="text-xs text-[#64748b] mb-1">Texto corrigido:</p>
                <textarea
                  value={textoEditado}
                  onChange={(e) => setTextoEditado(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl bg-[#f4f5f8] border border-[#e8eaf0] text-sm text-[#333A56] focus:outline-none focus:border-[#333A56]/40 resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={fecharModal}
                  className="flex-1 py-3 rounded-xl border border-[#e8eaf0] text-[#333A56] font-semibold text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={editarEAprovar}
                  disabled={salvando || !textoEditado.trim()}
                  className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-bold text-sm disabled:opacity-40 transition"
                >
                  {salvando ? "Salvando…" : "Salvar e Aprovar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Rejeitar */}
      {modal?.tipo === "rejeitar" && (
        <div className="fixed inset-0 z-[999] bg-black/50 backdrop-blur-sm flex items-end justify-center p-4">
          <div className="w-full max-w-lg rounded-3xl bg-white border border-[#e8eaf0] overflow-hidden shadow-2xl">
            <div className="px-5 pt-5 pb-3 flex items-center justify-between">
              <p className="font-bold text-[#333A56]">❌ Rejeitar Ocorrência</p>
              <button
                onClick={fecharModal}
                className="w-8 h-8 rounded-lg bg-[#f4f5f8] flex items-center justify-center text-[#64748b]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 pb-5 space-y-4">
              <div>
                <p className="text-xs text-[#64748b] mb-1">Motivo da rejeição:</p>
                <textarea
                  value={motivoRejeicao}
                  onChange={(e) => setMotivoRejeicao(e.target.value)}
                  rows={3}
                  placeholder="Descreva o motivo da rejeição…"
                  className="w-full px-4 py-3 rounded-xl bg-[#f4f5f8] border border-[#e8eaf0] text-sm text-[#333A56] placeholder:text-[#64748b] focus:outline-none focus:border-[#333A56]/40 resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={fecharModal}
                  className="flex-1 py-3 rounded-xl border border-[#e8eaf0] text-[#333A56] font-semibold text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={rejeitar}
                  disabled={salvando || !motivoRejeicao.trim()}
                  className="flex-1 py-3 rounded-xl bg-red-600 text-white font-bold text-sm disabled:opacity-40 transition"
                >
                  {salvando ? "Salvando…" : "Confirmar Rejeição"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
